import { ThemeProvider } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Hidden from '@mui/material/Hidden';
import Toolbar from '@mui/material/Toolbar';
import clsx from 'clsx';
import { memo, useCallback } from 'react';
import { selectToolbarTheme, useSettingsSlice } from 'app/store/settingsSlice';
import { useNavbarSlice, selectNavbar } from 'app/store/navbarSlice';
import NotificationPanelToggleButton from 'app/shared-components/notificationPanel/NotificationPanelToggleButton';
import NorthButton from 'app/shared-components/NorthButton';
import { Link, useLocation } from 'react-router-dom';
import NavbarToggleButton from '../../shared-components/NavbarToggleButton';
import UserMenu from '../../shared-components/UserMenu';
import SettingButton from '../../shared-components/SettingButton';
import useAuthUser from '../../../hooks/useAuthUser';
import { useCurrentProcessor } from 'src/app/queries/processor.queries';
import { useCurrentMerchant } from 'src/app/queries/merchant.queries';
import CopyReferralLinkButton from 'app/shared-components/CopyReferralLinkButton';
import useRestrictedMccStore from 'app/store/restrictedMccSlice';
import type { ILeadsItem } from 'src/app/types/ILeads';

function useCurrentUser() {
  const authUser = useAuthUser();
  const { data: processor } = useCurrentProcessor({ enabled: authUser?.role === 'processor' });
  const { data: merchant } = useCurrentMerchant({ enabled: authUser?.role === 'merchant' });

  const user = merchant?.data || processor?.data;
  let lead: ILeadsItem | undefined = undefined;

  if (user && typeof user === 'object') {
    if ('lead' in user && user.lead && typeof user.lead === 'object') {
      lead = user.lead as ILeadsItem;
    } else if (
      'leads' in user &&
      Array.isArray(user.leads) &&
      user.leads.length > 0 &&
      typeof user.leads[0] === 'object'
    ) {
      lead = user.leads[0] as ILeadsItem;
    }
  }

  return { user, lead };
}

function ToolbarLayout(props) {
  const config = useSettingsSlice((store) => store.current.layout.config);
  const toolbarTheme = useSettingsSlice(selectToolbarTheme);
  const navbar = useNavbarSlice(selectNavbar);
  const restrictedMccIds = useRestrictedMccStore((state) => state.restrictedMccIds);

  const { user, lead } = useCurrentUser();
  const authUser = useAuthUser();
  const { data: merchantData } = useCurrentMerchant({ enabled: authUser?.role === 'merchant' });
  const merchant = merchantData?.data;
  const industry = lead?.industry;

  let allowPeptideNorth = false;
  if (Number(industry) === 1736) {
    allowPeptideNorth = Boolean(
      lead?.isPeptideLicensedOrCertified && lead?.hasPeptideSalesExperience,
    );
  }

  const userRole = authUser?.role;
  const { pathname } = useLocation();

  const getLink = useCallback(() => {
    if (userRole === 'processor') return 'processor/preferences';
    return 'merchant/profile';
  }, [userRole]);

  return (
    <ThemeProvider theme={toolbarTheme}>
      <AppBar
        id="fuse-toolbar"
        className={clsx('flex relative z-20 shadow-md', props.className)}
        color="default"
        sx={{
          backgroundColor: (theme) =>
            theme.palette.mode === 'light'
              ? toolbarTheme.palette.background.paper
              : toolbarTheme.palette.background.default,
        }}
        position="static"
      >
        <Toolbar className="p-0 min-h-48 md:min-h-64">
          <div className="flex flex-1 px-16">
            {config.navbar.display && (
              <>
                <Hidden lgDown>
                  {!navbar.open && <NavbarToggleButton className="w-40 h-40 p-0 mx-0" />}
                </Hidden>

                <Hidden lgUp>
                  <NavbarToggleButton className="w-40 h-40 p-0 mx-0 sm:mx-8" />
                </Hidden>
              </>
            )}
            {pathname === '/merchant/dashboard' && (
              <div className="md:flex items-center gap-12 text-16 font-600 md:text-24 hidden">
                Welcome,{' '}
                {merchant
                  ? merchant.firstName && merchant.lastName
                    ? `${merchant.firstName} ${merchant.lastName}`
                    : merchant.businessName || merchant.user.email.split('@')[0]
                  : user?.user.email.split('@')[0]}
                !
              </div>
            )}
          </div>

          <div className="flex gap-8 md:gap-0 items-center px-8 h-full overflow-x-auto">
            {userRole === 'agent' && <CopyReferralLinkButton />}

            {userRole === 'merchant' &&
              industry &&
              ((Number(industry) === 1736 && allowPeptideNorth) ||
                !restrictedMccIds.includes(Number(industry))) && <NorthButton />}

            {userRole === 'merchant' && <NotificationPanelToggleButton />}

            {userRole !== 'admin' && userRole !== 'agent' && userRole !== 'manager' && (
              <Link to={getLink()}>
                <SettingButton />
              </Link>
            )}

            <UserMenu />
          </div>
        </Toolbar>
      </AppBar>
    </ThemeProvider>
  );
}

export default memo(ToolbarLayout);
